import { ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import Grid from '@mui/material/Grid';
import { Box, CircularProgress } from '@mui/material';
import { InfoRounded } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

// Components
import Button from 'components/Button/Button';

// Styles
import styles from './ContentBlueprintDropzone.module.scss';

// Store + Core
import { uploadBoardContentOutlines } from 'store/bct/actions';
import { isFileProcessingSelector } from 'store/bct/selectors';
import { FileTypesEnum, MimeTypes } from 'core/enums';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'store';
import { PARSAction } from 'core/models';

// Types
interface IProps {
  onProceed(): void;
}

export const ContentBlueprintDropzone = (props: IProps): ReactElement => {
  const { onProceed } = props;
  const dispatch: ThunkDispatch<AppState, unknown, PARSAction> = useDispatch();
  const { id } = useParams<{ id: string }>();

  const onDrop = useCallback(
    async (files: File[]): Promise<void> => {
      await dispatch(uploadBoardContentOutlines(id, { file: files }));

      // Close the modal when done.
      await onProceed();
    },
    [dispatch, id, onProceed],
  );

  const isFileProcessing: boolean = useSelector(isFileProcessingSelector);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { [MimeTypes.XLSX]: [FileTypesEnum.XLSX] },
    onDrop,
  });

  return (
    <Box component="div">
      <Grid container>
        <Grid className={styles.error} alignItems="center" size={{ xs: 12 }}>
          <Box sx={{ display: 'flex' }}>
            <InfoRounded />
            <span className={styles.message}>Batch upload will overwrite any existing content blueprint.</span>
          </Box>
        </Grid>
      </Grid>
      <Box component="div" sx={{ marginTop: 4 }}>
        <Grid container sx={{ justifyContent: 'center' }}>
          <Grid className={styles.dropzone} {...getRootProps()} id="transition-modal-description" size={12}>
            {isFileProcessing ? (
              <CircularProgress color="inherit" />
            ) : (
              <>
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p>Drop the files here ...</p>
                ) : (
                  <p>
                    Drop your completed files here, or <span>choose file</span>
                  </p>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row-reverse', marginTop: 4 }}>
        <Button className="primary" onClick={onProceed}>
          Done
        </Button>
      </Box>
    </Box>
  );
};
